import { Analytics, type AnalyticsInstance } from "analytics";
import type { Properties } from "posthog-js";
import type { AnalyticsEventName, AnalyticsEventPayloads } from "./analyticsEvents";
import { posthogAnalyticsPlugin } from "./posthogAnalyticsPlugin";

type StAnalytics = Omit<AnalyticsInstance, "track"> & {
  track: <Key extends AnalyticsEventName>(eventName: Key, payload: AnalyticsEventPayloads[Key]) => Promise<unknown>;
  plugins: {
    posthog: {
      captureException: (error: unknown, properties?: Properties) => void;
      reset: () => void;
      setPersonProperties: (properties: Properties) => void;
      group: (type: string, key: string) => void;
      register: (properties: Properties) => void;
    };
  };
};

/**
 * Singleton for sending analytics events
 *
 * Targets:
 *  - posthog
 *
 * Using https://github.com/DavidWells/analytics as an abstraction in case we want multiple targets
 * in the future
 */
export const stAnalytics: StAnalytics = Analytics({
  app: "storytell-webconsole",
  plugins: [posthogAnalyticsPlugin()],
  // Enables debugging in dev mode, it uses redux internally so we can use the Redux Dev Tools extension to
  // see al events being fired and a timeline for all events
  // https://chromewebstore.google.com/detail/redux-devtools/lmhkpmbekcpmknklioeibfkpmmfibljd?hl=en
  // @ts-ignore - import.meta.env is provided by Vite; typed in some but not all consumer tsconfigs
  debug: import.meta.env.DEV,
}) as StAnalytics;

export const captureException = (error: unknown, properties?: Properties) =>
  stAnalytics.plugins.posthog.captureException(error, properties);
